import { useState } from 'react';
import { API_CONFIG, HTTP_METHODS } from '../config/api';
import { useAuth } from './useAuth';

// Custom hook for wishlist API calls
export const useWishlistApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { getValidToken } = useAuth();

  // Generic API call function
  const makeApiCall = async (endpoint, options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const {
        method = HTTP_METHODS.GET,
        headers = {},
        body = null,
        baseUrl = API_CONFIG.BASE_URL,
      } = options;

      // Get auth token from localStorage
      const token = localStorage.getItem('authToken');

      // Prepare headers
      const requestHeaders = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...headers,
      };

      // Prepare request config
      const config = {
        method,
        headers: requestHeaders,
      };

      // Add body for POST/PUT requests
      if (body && (method === HTTP_METHODS.POST || method === HTTP_METHODS.PUT)) {
        config.body = JSON.stringify(body);
      }

      const response = await fetch(`${baseUrl}${endpoint}`, config);
      
      // Safe JSON parsing
      const contentType = response.headers.get('content-type');
      let data = null;
      
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            console.error('JSON parse error:', parseError);
            data = { message: 'Invalid JSON response' };
          }
        } else {
          data = {};
        }
      } else {
        const text = await response.text();
        data = { message: text || 'No response data' };
      }

      if (response.ok) {
        return { success: true, data: data || {} };
      } else {
        const errorMessage = data?.message || `HTTP Error: ${response.status}`;
        setError(errorMessage);
        return { 
          success: false, 
          message: errorMessage, 
          errors: data?.errors || [] 
        };
      }
    } catch (err) {
      const errorMessage = err.message || 'Network error occurred';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Add to Wishlist API call
  const addToWishlistApi = async (customerId, productId, productVariantId, priceAtAdd) => {
    const result = await makeApiCall(API_CONFIG.WISHLIST.ADD, {
      method: HTTP_METHODS.POST,
      body: {
        customerId,
        productId,
        productVariantId,
        priceAtAdd
      }
    });

    if (result.success) {
      return {
        success: true,
        wishlistItemId: result.data.id,
        message: result.data.message || 'Product added to wishlist',
        data: result.data
      };
    }

    return result;
  };

  // Get Wishlist Items API call
  const getWishlistItemsApi = async (customerId) => {
    const result = await makeApiCall(`${API_CONFIG.WISHLIST.GET}/${customerId}`, {
      method: HTTP_METHODS.GET
    });

    if (result.success) {
      return {
        success: true,
        wishlistItems: result.data,
        data: result.data
      };
    }

    return result;
  };

  // Remove from Wishlist API call (without global loading state)
  const removeFromWishlistApi = async (wishlistItemId) => {
    // Don't use makeApiCall to avoid global loading state
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.WISHLIST.REMOVE}/${wishlistItemId}`, {
        method: HTTP_METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      // Safe JSON parsing
      const contentType = response.headers.get('content-type');
      let data = null;
      
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            console.error('JSON parse error:', parseError);
            data = { message: 'Invalid JSON response' };
          }
        } else {
          data = {};
        }
      } else {
        const text = await response.text();
        data = { message: text || 'No response data' };
      }

      if (response.ok) {
        return {
          success: true,
          message: data?.message || 'Item removed from wishlist',
          data: data
        };
      } else {
        const errorMessage = data?.message || `HTTP Error: ${response.status}`;
        return { 
          success: false, 
          message: errorMessage, 
          errors: data?.errors || [] 
        };
      }
    } catch (err) {
      const errorMessage = err.message || 'Network error occurred';
      return { success: false, message: errorMessage };
    }
  };

  // Clear Wishlist API call
  const clearWishlistApi = async (customerId) => {
    const result = await makeApiCall(`${API_CONFIG.WISHLIST.CLEAR}/${customerId}`, {
      method: HTTP_METHODS.POST
    });

    if (result.success) {
      return {
        success: true,
        message: result.data?.message || 'Wishlist cleared successfully',
        data: result.data
      };
    }

    return result;
  };

  return {
    loading,
    error,
    addToWishlistApi,
    getWishlistItemsApi,
    removeFromWishlistApi,
    clearWishlistApi,
    makeApiCall
  };
};